import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { api, type SearchHit } from '../lib/api.js';
import { useGm } from '../hooks/useGm.js';
import { setGmSecret } from '../lib/gm.js';
import { useTheme } from '../hooks/useTheme.js';
import { DvIcon, WindMark } from './DvIcon.js';
import { WORLD } from '../lib/world.js';

const NAV = WORLD.nav;
const CREATE_TYPES = WORLD.createTypes;

export function AppHeader(): JSX.Element {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<SearchHit[] | null>(null);
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const boxRef = useRef<HTMLDivElement | null>(null);
  const createRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { isGm } = useGm();
  const { theme, cycle } = useTheme();

  useEffect(() => {
    if (q.trim().length < 2) { setHits(null); return; }
    const t = setTimeout(() => {
      api.wikiSearch(q, 12).then((r) => setHits(r.hits)).catch(() => setHits([]));
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    function onClick(e: MouseEvent): void {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
      if (createRef.current && !createRef.current.contains(e.target as Node)) setCreateOpen(false);
    }
    function onKey(e: KeyboardEvent): void {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  function isActive(key: string, to: string): boolean {
    if (key === 'world') return pathname === '/wiki';
    return pathname.startsWith(to);
  }

  function resetSearch(): void {
    setQ(''); setHits(null); setOpen(false);
  }

  function toggleMaster(): void {
    if (isGm) {
      if (window.confirm('Вийти з режиму Майстра?')) setGmSecret('');
    } else {
      const secret = window.prompt('Введіть секрет Майстра:');
      if (secret) setGmSecret(secret.trim());
    }
  }

  return (
    <header className="dv-topbar">
      <div className="dv-topbar-inner">
        <Link to="/wiki" className="dv-brand">
          <span className="dv-brand-mark"><WindMark /></span>
          <span className="dv-brand-words">
            <span className="dv-brand-name">{WORLD.brandName}</span>
            <span className="dv-brand-sub">{WORLD.brandSub}</span>
          </span>
        </Link>

        <nav className="dv-nav">
          {NAV.map((n) => {
            if (n.gm && !isGm) {
              return <button key={n.key} className="dv-tab" disabled title="Лише Майстер">{n.label}</button>;
            }
            return (
              <Link
                key={n.key}
                to={n.to}
                className={`dv-tab ${isActive(n.key, n.to) ? 'active' : ''}`}
                aria-current={isActive(n.key, n.to) ? 'page' : undefined}
              >
                {n.label}
              </Link>
            );
          })}
        </nav>

        <div className="dv-navright">
          {isGm && (
            <div className="dv-create-wrap" ref={createRef}>
              <button
                type="button"
                className="dv-create"
                onClick={() => setCreateOpen((v) => !v)}
                onKeyDown={(e) => { if (e.key === 'Escape') setCreateOpen(false); }}
                aria-haspopup="menu"
                aria-expanded={createOpen}
                aria-label="Створити нову сторінку"
              >
                <DvIcon name="plus" size={16} stroke={2.2} /> Створити
              </button>
              {createOpen && (
                <div className="dv-create-dropdown" role="menu">
                  <Link className="dv-create-item dv-create-item--blank" role="menuitem" to="/wiki/new" onClick={() => setCreateOpen(false)}>
                    <DvIcon name="edit" size={16} /> Порожня сторінка
                  </Link>
                  <div className="dv-create-sep" />
                  {CREATE_TYPES.map((c) => (
                    <Link
                      key={c.type}
                      className="dv-create-item"
                      role="menuitem"
                      to={`/wiki/new?type=${c.type}&treePath=${encodeURIComponent(c.tree)}&category=${encodeURIComponent(c.category)}`}
                      onClick={() => setCreateOpen(false)}
                    >
                      <DvIcon name={c.icon} size={16} /> {c.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="dv-search" ref={boxRef}>
            <DvIcon name="search" size={16} />
            <input
              ref={inputRef}
              type="text"
              value={q}
              aria-label="Пошук у вікі"
              onChange={(e) => { setQ(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && hits && hits[0]) {
                  navigate(`/wiki/${encodeURIComponent(hits[0].slug)}`);
                  resetSearch();
                  (e.target as HTMLInputElement).blur();
                }
                if (e.key === 'Escape') { setOpen(false); (e.target as HTMLInputElement).blur(); }
              }}
              placeholder="Пошук"
            />
            <span className="dv-kbd" aria-hidden="true">⌘K</span>
            {open && q.trim().length >= 2 && (
              <div className="wiki-search-results" role="listbox">
                {hits === null && <div className="empty">шукаю…</div>}
                {hits !== null && hits.length === 0 && <div className="empty">нічого не знайдено</div>}
                {hits !== null && hits.map((h) => (
                  <Link key={h.slug} to={`/wiki/${encodeURIComponent(h.slug)}`} className="hit" onClick={() => setOpen(false)}>
                    <div>
                      <span className="title">{h.title}</span>
                      {h.category && <span className="cat">{h.category}</span>}
                    </div>
                    {h.snippet && (
                      <div className="snippet" dangerouslySetInnerHTML={{ __html: sanitizeSnippet(h.snippet) }} />
                    )}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <button type="button" className="dv-iconbtn" onClick={cycle}
            title={theme === 'dark' ? 'Темна тема (клік: → Світла)' : 'Світла тема (клік: → Темна)'}
            aria-label="Перемкнути тему">
            <DvIcon name={theme === 'dark' ? 'sun' : 'moon'} size={18} />
          </button>

          <button
            type="button"
            className={`dv-master ${isGm ? 'on' : ''}`}
            onClick={toggleMaster}
            aria-pressed={isGm}
            aria-label={isGm ? 'Режим Майстра увімкнено — вийти' : 'Увімкнути режим Майстра'}
            title="Режим Майстра"
          >
            <DvIcon name="gmeye" size={17} /> <span>МАЙСТЕР</span>
          </button>
        </div>
      </div>
    </header>
  );
}

function sanitizeSnippet(s: string): string {
  const escaped = s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return escaped.replace(/&lt;mark&gt;/g, '<mark>').replace(/&lt;\/mark&gt;/g, '</mark>');
}
